/* eslint-disable lingui/no-unlocalized-strings */

import config from '@/config';
import { type Organization } from '@/models/organization';
import { type UserFromGetSession } from '@/models/user';
import {
  useClientToken,
  useCurrentOrganization,
  useCurrentSession,
  useStoreToken,
} from '@/services/auth';
import { captureMessage } from '@/services/sentry';
import { HttpError } from '@/utils/error/HttpError';
import { NotFoundError } from '@/utils/error/NotFoundError';

import { checkHealthAndRedirect } from './health';

type ContentType = 'application/json' | 'multipart/form-data';

export const useFetch = <
  T extends {
    response: any;
    body: any;
    query: any;
  } = any,
>() => {
  const [currentOrganization] = useCurrentOrganization();
  const { currentSession } = useCurrentSession();
  const clientToken = useClientToken();
  const storeToken = useStoreToken();

  return fetchApi<T['response'], T['body'], T['query']>(
    currentOrganization,
    currentSession,
    clientToken,
    storeToken
  );
};

const fetchApi =
  <
    Response = any,
    Body = any,
    Params =
      | Record<string, string | string[] | ReadonlyArray<string> | number | boolean | undefined>
      | undefined,
  >(
    currentOrganization: Organization | null,
    currentSession?: UserFromGetSession | null,
    clientToken?: string,
    storeToken?: string
  ) =>
  <ResponseOverride = Response>(
    path: string,
    params?: Params,
    options: Omit<RequestInit, 'body'> & { body?: Body } = { method: 'GET' },
    contentType: ContentType = 'application/json'
  ) => {
    const searchParams = [];

    for (const [key, value] of Object.entries(params || {})) {
      if (Array.isArray(value)) {
        for (const item of value) {
          const formattedItem = typeof item === 'boolean' ? item : formatParamValue(item);

          searchParams.push(`${key}[]=${formattedItem}`);
        }
      } else if (typeof value === 'boolean') {
        if (value === false) {
          searchParams.push(`${key}=0`);
        } else {
          searchParams.push(`${key}=1`);
        }
      } else if (value !== undefined) {
        searchParams.push(`${key}=${formatParamValue(value)}`);
      }
    }

    let formattedBody: string | FormData | undefined;

    if (options?.body && contentType === 'application/json') {
      formattedBody = JSON.stringify(options.body);
      options.headers = {
        'Content-Type': contentType,
        ...options.headers,
      };
    } else if (options?.body && contentType === 'multipart/form-data') {
      const formData = new FormData();

      for (const key in options.body) {
        formData.append(key, options.body[key] as string | Blob);
      }

      formattedBody = formData;
    }

    if (currentOrganization) {
      options.headers = {
        'prolong-organization': currentOrganization.slug,
        ...options.headers,
      };
    }

    if (clientToken) {
      options.headers = {
        'client-token': clientToken,
        ...options.headers,
      };
    }

    if (storeToken) {
      options.headers = {
        'store-token': storeToken,
        ...options.headers,
      };
    }

    const pathAndParams = `${!path.startsWith('/') ? '/' : ''}${path}${searchParams.length ? `?${searchParams.join('&')}` : ''}`;
    const url = `${config.apiUrl}${pathAndParams}`;

    return window
      .fetch(url, {
        credentials: 'include',
        ...options,
        body: formattedBody,
        headers: {
          ...options.headers,
          'App-Version': config.appVersion,
          'Health-Ignore': config.health.ignore ? '1' : '0',
        },
      })
      .then(async (res) => {
        /**
         * This value is used in app/src/components/Link/Link.tsx to know if we should force a full reload of the app on the next navigation.
         */
        window.appVersionLatest = res.headers.get('App-Version-Latest');

        const textBody = await res.text();
        let body;

        try {
          body = JSON.parse(textBody);
        } catch {
          body = textBody;
        }

        if (shouldCaptureMessageToSentry(res, body)) {
          captureMessage(`Error: ${options.method ?? 'GET'} ${pathAndParams}`, (context) => {
            context.setLevel('error');
            context.setExtra(
              'organization',
              currentOrganization
                ? {
                    id: currentOrganization.id,
                    name: currentOrganization.name,
                  }
                : null
            );
            context.setExtra(
              'user',
              currentSession
                ? {
                    id: currentSession.id,
                    email: currentSession.email,
                  }
                : null
            );
            context.setExtra('request-body', options.body);
            context.setExtra('response-body', body);

            return context;
          });
        }

        if (!res.ok) {
          if (res.status === 403) {
            if (options.method === 'GET' && path === '/notifications') {
              if (!document.cookie.includes('__session_exists')) {
                // GET /notifications is called on windows refocus, with regular polling.
                // If the endpoints returns 403, it may mean that the user is not connected anymore.
                // Therefore, we check the "__session_exists" cookie. If it's not present, it means that the
                // browser has removed the session cookie (probably because the user logged out in another tab).
                // In this case, we reload the page to force the user to log in again.
                window.location.reload();
              }
            }
          }

          if (res.status === 404) {
            // Throw a specific error because some components have a dedicated state/view for this
            throw new NotFoundError(`${body?.message ?? body}`);
          }

          if (res.status === 503) {
            // Service Unavailable - Check health and possibly redirect to health page
            await checkHealthAndRedirect();
          }

          throw new HttpError(`${body?.message ?? body}`, res.status);
        }

        return body as ResponseOverride;
      });
  };

/**
 * Encode special characters so they are preserved in the query string (e.g. "+" that would be read as a space)
 */
function formatParamValue(value: unknown): string | undefined {
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  return value ? encodeURIComponent(value.toString()) : value?.toString();
}

function shouldCaptureMessageToSentry(res: Response, body: any) {
  if (res.ok) {
    return false;
  }

  // 403 and 401 are special cases; we don't want to log it as it's a normal "error" users can
  // have if they don't have the right permissions or password.
  // 404 errors most often happen when someone who has multiple accounts opens a link to
  // a request while being logged in with the wrong account.
  if (res.status === 401 || res.status === 403 || res.status === 404) {
    return false;
  }

  if (['bridge_error', 'user_handled_error'].includes((body?.error as string) ?? '')) {
    console.log('Ignored API error, not capturing to Sentry', body);

    return false;
  }

  // These are legacy errors with no user impact that we do not want to capture to Sentry
  // Ideally, we should fix the errors instead of ignoring them
  const messagesToIgnore = [
    // From get-client-shipping-options.action.ts, when fetching client shipping option while the article is already in transit step
    'There are no transit steps in the future path of this article',
    'The origin of the next transit step is not the client',
  ];

  if (messagesToIgnore.some((message) => body?.message?.includes?.(message))) {
    return false;
  }

  return true;
}
